import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { dashboardApi } from '@/domains/traffic-monitoring/apis/dashboard';
import type { TrafficExportParams } from '@/domains/traffic-monitoring/types/dashboard.types';
import { readBlobErrorMessage, saveBlob } from '@/shared/utils/download';

// PSC_PC0203001_BE_008 / BE_009 / PSC_PC0204002_BE_001. The file name mirrors
// the one the backend puts in Content-Disposition, which the response
// interceptor drops along with the rest of the headers on its way to returning
// just the body - hence `filePrefix` being derived from the same params.
export type TrafficExportKind = 'volume' | 'event' | 'cctv';

const EXPORTS: Record<
  TrafficExportKind,
  {
    request: (params: TrafficExportParams) => Promise<Blob>;
    filePrefix: (params: TrafficExportParams) => string;
  }
> = {
  volume: { request: dashboardApi.exportTrafficVolume, filePrefix: () => 'traffic-volume' },
  event: { request: dashboardApi.exportTrafficEvent, filePrefix: () => 'traffic-event' },
  cctv: {
    request: dashboardApi.exportTrafficCctv,
    filePrefix: (params) => `traffic-cctv-${params.cctv_id}`,
  },
};

// English copy for each message key the export endpoints can answer with, used
// until the tag is loaded from the language table. Anything unrecognised - a
// network drop, an unmapped 500 - falls back to the generic line.
const ERROR_FALLBACKS: Record<string, string> = {
  'msg > export data limit exceeded':
    'Export Failed, data limit exceeded. Please select a shorter date range.',
  'msg > export start date is after end date':
    'Export failed, the start date is after the end date.',
  'msg > invalid start date': 'Export failed, the start date is invalid.',
  'msg > invalid end date': 'Export failed, the end date is invalid.',
  'msg > invalid interval': 'Export failed, the interval is invalid.',
  'msg > elasticsearch unavailable': 'Export failed, the data service is unavailable.',
  'msg > export template missing': 'Export failed, the report template is unavailable.',
  'msg > cctv id is required': 'Export failed, no camera was selected.',
  'msg > invalid cctv id': 'Export failed, no camera was selected.',
  'msg > no such data': 'Export failed, that camera no longer exists.',
};
const GENERIC_ERROR = 'Export failed. Please try again.';

const compact = (isoDate: string) => isoDate.replace(/-/g, '');

/**
 * `extraParams` carries whatever the endpoint needs beyond the range - the
 * per-CCTV export's `cctv_id`. Passed as a plain object at call time rather
 * than held in the hook, so it never churns the memoized `runExport`.
 */
export const useTrafficExport = (kind: TrafficExportKind) => {
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const clearError = useCallback(() => setErrorMessage(null), []);

  const runExport = useCallback(
    async (
      startDate: string,
      endDate: string,
      extraParams: Omit<TrafficExportParams, 'start_date' | 'end_date'> = {}
    ) => {
      const { request, filePrefix } = EXPORTS[kind];
      const params: TrafficExportParams = {
        ...extraParams,
        start_date: startDate,
        end_date: endDate,
      };
      setIsExporting(true);
      setErrorMessage(null);
      try {
        const blob = await request(params);
        saveBlob(blob, `${filePrefix(params)}_${compact(startDate)}-${compact(endDate)}.xlsx`);
        return true;
      } catch (error) {
        // The backend names the failure with a message key (a too-wide range,
        // a bad date, ES down); anything else lands on the generic line.
        const serverMessage = await readBlobErrorMessage(error);
        setErrorMessage(
          serverMessage
            ? t(serverMessage, ERROR_FALLBACKS[serverMessage] ?? GENERIC_ERROR)
            : t('msg > export failed', GENERIC_ERROR)
        );
        return false;
      } finally {
        setIsExporting(false);
      }
    },
    [kind, t]
  );

  return { runExport, isExporting, errorMessage, clearError };
};
